import { Vector3 } from "../js/Vector3";
import vtkMath from "vtk.js/Sources/Common/Core/Math";
import { vec3 } from "gl-matrix";
import vtkPlaneSource from 'vtk.js/Sources/Filters/Sources/PlaneSource';
import { Representation } from 'vtk.js/Sources/Rendering/Core/Property/Constants';
import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import vtkMapper from 'vtk.js/Sources/Rendering/Core/Mapper';

import { useEffect, useRef } from 'react';
import { Union } from "fable-compiler/bin/fable-library/Types";

import { logDebug, constants, debugCallback, getTopLevelDUName } from '../js/Common';

const isDebugging = false;

function log(msg, ...params) {
    logDebug("MeshVisualization.SlicePlane", isDebugging, msg, ...params);
}

function debug(callback) {
    debugCallback(isDebugging, callback);
}

// Corresponds to the DicomViewMode DU in F#.
const DicomViewMode = Object.freeze({
    Axial: "Axial",
    Coronal: "Coronal",
    Sagittal: "Sagittal",
    StretchedCpr: "StretchedCpr",
    StraightenedCpr: "StraightenedCpr",
    CenterlinePlaneA: "CenterlinePlaneA",
    CenterlinePlaneB: "CenterlinePlaneB"
});

/**
 * @typedef {Object} SlicePlaneProps
 * @property {number|null} [index] - The current index of the slice plane.
 * @property {number|null} [maxIndex] - The maximum index of the slice plane.
 * @property {number[]|null} [bounds] - The bounds of the slice plane as an array of numbers.
 * @property {boolean} isVisible - Whether the slice plane is visible.
 * @property {boolean} isDisabled - Whether the slice plane is disabled.
 * @property {SliceCenterlinePlane|null} [centerlinePlane] - The centerline plane associated with the slice plane.
 * @property {Union} dicomViewMode - The view mode of the slice plane (e.g., Axial, Coronal, Sagittal, CenterlinePlaneA, etc.).
 */

/**
 * @typedef {Object} SliceCenterlinePlane
 * @property {PlanePipeline} PlanePipeline - The pipeline associated with the centerline plane.
 * @property {ICenterlineMeasurementUpdater} CenterlinePlaneUpdater - The updater for the centerline plane.
 */

/**
 * @typedef {Object} PlanePipeline
 * @property {CenterlinePlane} centerlinePlane - The centerline plane data.
 * @property {CenterlinePlaneSource} centerlinePlaneSource - The source of the centerline plane.
 */

/**
 * @typedef {Object} CenterlinePlane
 * @property {Union} centerlineType - The type of the centerline (e.g., Wall).
 * @property {Union} branchSelection - The branch selection for the centerline.
 * @property {number} index - The index of the centerline plane.
 * @property {string} callbackIdentifier - A unique identifier for callbacks.
 * @property {Union} planeIdentifier - The identifier for the plane (e.g., "First", "Second", "Third"). Tied to the PlaneIdentifier DU in F#.
 */

/**
 * @typedef {Object} CenterlinePlaneSource
 * @property {IFilter} rootFilter - The root filter for the centerline plane source.
 * @property {IPlaneIndexFilter} planeIndexFilter - The filter for the plane index.
 */

/**
 * @typedef {Object} IFilter
 * @property {function(number): Object} getOutputData - A method to get output data based on an index.
 */

/**
 * @typedef {Object} IPlaneIndexFilter
 * @extends IFilter
 * @property {function(number): void} setCurrentIdx - Sets the current index for the plane.
 */

/**
 * @typedef {Object} ICenterlineMeasurementUpdater
 * @property {function(): UnifiedBranchIndexBounds} getUnifiedBranchIndexBounds - A method to get unified branch index bounds.
 */

/**
 * @typedef {Object} UnifiedBranchIndexBounds
 * @property {number} min - The minimum bound.
 * @property {number} max - The maximum bound.
 */

/**
 * Configures the visual properties of a plane actor.
 *
 * @param {vtkActor} planeActor - The actor representing the plane to be configured.
 */
function configurePlaneActor(planeActor) {
    planeActor.getProperty().setEdgeVisibility(true);
    planeActor.getProperty().setLighting(false);
    planeActor.getProperty().setRepresentation(Representation.WIREFRAME);
}

/**
 * Creates and manages a slice plane visualization.
 *
 * @param {Object} genericRenderWindow - The render window to which the slice plane will be added.
 * @param {boolean} isStentPlanning
 * @param {SlicePlaneProps} slicePlaneProps - The properties for configuring the slice plane.
 */
export function useSlicePlane(
    genericRenderWindow,
    isStentPlanning,
    slicePlaneProps) {
    log("slicePlaneProps:", slicePlaneProps);

    const context = useRef(null);

    const {
        bounds = null,
        isVisible = true,
        isDisabled = false,
        centerlinePlane = null,
        dicomViewMode = DicomViewMode.Axial
    } = slicePlaneProps || {};

    // Effect to handle mounting and unmounting
    useEffect(() => {
        log("Mounting slice plane");

        return () => {
            // Just using a more convenient name; no magic here.
            const ctx = context.current;

            if (ctx) {
                if (ctx.genericRenderWindow) {
                    const renderer = ctx.genericRenderWindow.getRenderer();

                    if (renderer && ctx.planeActor) {
                        log(`Removing ${getTopLevelDUName(dicomViewMode)} plane actor`);
                        renderer.removeActor(ctx.planeActor);
                    }

                    if (renderer && ctx.centerlinePlaneActor) {
                        log("Removing centerline plane actor");
                        renderer.removeActor(ctx.centerlinePlaneActor);
                    }
                }

                if (ctx.planeActor) {
                    log(`Deleting ${getTopLevelDUName(dicomViewMode)} plane`);
                    ctx.planeActor.delete();
                    ctx.planeMapper.delete();
                }

                if (ctx.centerlinePlaneActor) {
                    log("Deleting centerline plane");
                    ctx.centerlinePlaneActor.delete();
                    ctx.centerlinePlaneMapper.delete();
                }

            }
            else if (!isStentPlanning) {
                console.warn("Unable to clean up slice plane since context.current is null");
            }
        };
    }, []);

    // Effect to render the axial and centerline slice planes.
    useEffect(() => {
        if (context.current) {
            context.current = {
                ...context.current,
                isStentPlanning
            }
        }

        if (isStentPlanning) {
            return;
        }

        if (genericRenderWindow) {
            if (context.current) {
                context.current = {
                    ...context.current,
                    genericRenderWindow
                }
            }

            if (centerlinePlane) {
                if (!context.current?.centerlinePlaneActor) {
                    log("Creating centerline plane");
                    const renderer = genericRenderWindow.getRenderer();
                    const planeSource = centerlinePlane.PlanePipeline.centerlinePlaneSource.planeIndexFilter;
                    const port = planeSource.getOutputPort();
                    const planeMapper = vtkMapper.newInstance();
                    const planeActor = vtkActor.newInstance();

                    planeMapper.setInputConnection(port);
                    planeActor.setMapper(planeMapper);
                    configurePlaneActor(planeActor);

                    switch (getTopLevelDUName(dicomViewMode)) {
                        case DicomViewMode.CenterlinePlaneA:
                            planeActor.getProperty().setColor(0, 0, 1);
                            planeActor.getProperty().setEdgeColor(0.5, 0.5, 1);
                            break;
                        case DicomViewMode.CenterlinePlaneB:
                            planeActor.getProperty().setColor(1, 0, 0);
                            planeActor.getProperty().setEdgeColor(1, 0.5, 0.5);
                            break;
                    }

                    if (isVisible && !isDisabled) {
                        renderer.addActor(planeActor);
                    }

                    context.current = {
                        ...context.current,
                        centerlinePlaneMapper: planeMapper,
                        centerlinePlaneActor: planeActor,
                        centerlinePlaneSource: planeSource
                    }
                }
                else {
                    log("Updating centerline plane")
                    const planeSource = centerlinePlane.PlanePipeline.centerlinePlaneSource.planeIndexFilter;
                    const port = planeSource.getOutputPort();
                    context.current.centerlinePlaneMapper.setInputConnection(port);
                    context.current.centerlinePlaneActor.setMapper(context.current.centerlinePlaneMapper);
                    context.current.centerlinePlaneSource = planeSource;
                }
            }
            else if (bounds) {
                // Axial / Coronal / Sagittal plane rendering.
                if (!context.current?.planeActor) {
                    const renderer = genericRenderWindow.getRenderer();
                    const boundingVectors = Vector3.ofBounds(bounds);
                    let planeSizeX, planeSizeY, planeSizeZ;
                    let point1, point2;

                    switch (getTopLevelDUName(dicomViewMode)) {
                        case DicomViewMode.Coronal:
                            planeSizeX =
                                vtkMath.subtract(boundingVectors.max.toVtk(),
                                    boundingVectors.min.toVtk(),
                                    vec3.create())[0];

                            planeSizeZ =
                                vtkMath.subtract(boundingVectors.max.toVtk(),
                                    boundingVectors.min.toVtk(),
                                    vec3.create())[2];

                            point1 = [0, 0, planeSizeZ];
                            point2 = [planeSizeX, 0, 0];
                            break;

                        case DicomViewMode.Sagittal:
                            planeSizeY =
                                vtkMath.subtract(boundingVectors.max.toVtk(),
                                    boundingVectors.min.toVtk(),
                                    vec3.create())[1];
                            planeSizeZ =
                                vtkMath.subtract(boundingVectors.max.toVtk(),
                                    boundingVectors.min.toVtk(),
                                    vec3.create())[2];

                            point1 = [0, planeSizeY, 0];
                            point2 = [0, 0, planeSizeZ];
                            break;

                        case DicomViewMode.Axial:
                        default:
                            planeSizeX =
                                vtkMath.subtract(boundingVectors.max.toVtk(),
                                    boundingVectors.min.toVtk(),
                                    vec3.create())[0] / 2;

                            planeSizeY =
                                vtkMath.subtract(boundingVectors.max.toVtk(),
                                    boundingVectors.min.toVtk(),
                                    vec3.create())[1] / 2;

                            point1 = [planeSizeX, 0, 0];
                            point2 = [0, planeSizeY, 0];
                            break;
                    }

                    const planeSource = vtkPlaneSource.newInstance({
                        xResolution: constants.planeResolution,
                        yResolution: constants.planeResolution,
                        point1,
                        point2
                    });

                    const planeMapper = vtkMapper.newInstance();
                    planeMapper.setInputConnection(planeSource.getOutputPort());

                    const planeActor = vtkActor.newInstance();
                    configurePlaneActor(planeActor);
                    planeActor.setMapper(planeMapper);

                    if (isVisible && !isDisabled) {
                        renderer.addActor(planeActor);
                    }

                    context.current = {
                        ...context.current,
                        planeMapper,
                        planeActor,
                        planeSource
                    };
                }

                log(`Updating ${getTopLevelDUName(dicomViewMode)} plane`);
                const boundingVectors = Vector3.ofBounds(bounds);
                const centerBounds = Vector3.midBounds(boundingVectors.min, boundingVectors.max);
                context.current.planeSource.setCenter(centerBounds.toVtk());

                let normal;

                switch (getTopLevelDUName(dicomViewMode)) {
                    case DicomViewMode.Coronal:
                        normal = Vector3.unitY();
                        break;
                    case DicomViewMode.Sagittal:
                        normal = Vector3.unitX();
                        break;
                    case DicomViewMode.Axial:
                    default:
                        normal = Vector3.unitZ();
                        break;
                }

                context.current.planeSource.setNormal(normal);
                context.current.planeSource.update();
            }

            genericRenderWindow.getRenderer().resetCameraClippingRange();
            genericRenderWindow.getRenderWindow().render();
        }
    }, [isStentPlanning, isVisible, isDisabled, bounds, centerlinePlane]);

    // Effect to show/hide the slice plane
    useEffect(() => {
        if (isStentPlanning) {
            return;
        }
        log(`Visibility effect firing with isVisible = ${isVisible}, bound and centerlinePlane:`, bounds, centerlinePlane);

        if (genericRenderWindow && context.current) {
            const renderer = genericRenderWindow.getRenderer();
            const renderWindow = genericRenderWindow.getRenderWindow();

            const actor =
                centerlinePlane ? context.current.centerlinePlaneActor : context.current.planeActor;

            if (isVisible === true && !isDisabled) {
                renderer.addActor(actor);
            } else {
                renderer.removeActor(actor);
            }

            renderer.resetCameraClippingRange();
            renderWindow.render();
        }
    }, [isStentPlanning, isVisible, isDisabled, centerlinePlane]);

    // Effect to remove the slice plane when the view mode changes.
    useEffect(() => {
        if (isStentPlanning) {
            return;
        }

        if (context.current && genericRenderWindow) {
            const renderer = genericRenderWindow.getRenderer();

            switch (getTopLevelDUName(dicomViewMode)) {
                case DicomViewMode.Axial:
                case DicomViewMode.Coronal:
                case DicomViewMode.Sagittal:
                    if (context.current.centerlinePlaneActor) {
                        renderer.removeActor(context.current.centerlinePlaneActor);
                    }
                    break;
                case DicomViewMode.CenterlinePlaneA:
                case DicomViewMode.CenterlinePlaneB:
                    if (context.current.planeActor) {
                        renderer.removeActor(context.current.planeActor);
                    }
                    break;
                default:
                    console.warn("Unsupported view mode:", getTopLevelDUName(dicomViewMode), dicomViewMode);
            }

            const renderWindow = genericRenderWindow.getRenderWindow();
            renderWindow.render();
        }
    }, [isStentPlanning, dicomViewMode]);
}

/**
 * React component to manage the slice plane visualization.
 * @param {Object} props - The properties for the slice plane component.
 * @param {Object} props.genericRenderWindow - The render window to which the slice plane will be added.
 * @param {boolean} props.isStentPlanning - Indicates if the current viewport is the stent planning viewport.
 * @param {SlicePlaneProps} props.slicePlaneProps - The properties for configuring the slice plane.
 */
 export function SlicePlane({ props }) {
    // This component does not render anything itself; it just manages the slice plane.
    const { genericRenderWindow, isStentPlanning, slicePlaneProps } = props || {};
    useSlicePlane(genericRenderWindow, isStentPlanning, slicePlaneProps)
    return null;
}